import React, { useCallback, useMemo, useState } from "react";

import { orderBy } from "lodash";

import useTranslations from "../../../hooks/useTranslations";

import { CheckIcon, EyeOpenIcon } from "../../icons";

import Select from "../../wombat-ui/components/Form/Select";

import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  VStack,
  HStack,
  Spacer,
} from "@chakra-ui/react";

import Question from "../../reports/_deprecated/Question";
import Section from "../../reports/_deprecated/Section";
import Favorite from "../../utils/Favorite";
import { useToggle } from "react-use";
import { H5 } from "../../wombat-ui";
import {
  TemplateFragment,
  SectionFragment,
  useInsertTemplate,
  ReportTypeEnum,
} from "../../../graphql/generated/definitions";

type TemplatePreviewModalProps = {
  template: TemplateFragment;
  isOpen: boolean;
  onClose: () => void;
  onSelect: () => void;
};

const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  template,
  isOpen,
  onClose,
  onSelect,
}) => {
  const t = useTranslations();
  const [selectedReportType, setSelectedReportType] = useState<
    ReportTypeEnum | "All"
  >("All");

  const reportTypeItem = useMemo(
    (): { value: ReportTypeEnum | "All"; label: string }[] => [
      { value: "All", label: t("reviews.allQuestions") as string },
      {
        value: "FirstReport",
        label: t("reviews.firstReportQuestions") as string,
      },
      {
        value: "IntermediateReport",
        label: t("reviews.intermediateReportQuestions") as string,
      },
      {
        value: "FinalReport",
        label: t("reviews.finalReportQuestions") as string,
      },
    ],
    []
  );

  const sectionPerReportType = useMemo(() => {
    const sections: {
      [key in ReportType | "All"]: SectionFragment[];
    } = {
      All: [],
      FirstReport: [],
      IntermediateReport: [],
      FinalReport: [],
    };

    const reportTypes: ReportType[] = [
      "FirstReport",
      "IntermediateReport",
      "FinalReport",
    ];

    orderBy(template.sections, "order").forEach((s) => {
      sections.All.push(s);

      reportTypes.map((rt) => {
        if (s.questions.some((a) => a.reportTypes.includes(rt))) {
          sections[rt].push({
            ...s,
            questions: s.questions.filter((a) => a.reportTypes.includes(rt)),
          });
        }
      });
    });

    return sections;
  }, [template]);

  return (
    <Modal
      isOpen={isOpen}
      aria-label="Template preview"
      onClose={onClose}
      data-test-id={`${template.id}-preview`}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t("reviews.templatePreviewTitle", { title: template.title })}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={8} align="stretch">
            <Select
              id="question-visibility-select"
              label={t("reviews.viewText") as string}
              items={reportTypeItem}
              defaultValue={reportTypeItem[0]}
              renderItem={(i) => i.label}
              searchable
              onChange={(i) => {
                setSelectedReportType(i.value);
              }}
              searchOption={{
                keys: ["label"],
                threshold: 0.2,
                useExtendedSearch: true,
              }}
              getValue={(i) => i.value}
              srOnlyLabel
            />

            {sectionPerReportType[selectedReportType].map(
              (section: SectionFragment) => (
                <Section section={section} key={section.id}>
                  {orderBy(section.questions, "order").map((question, index) => (
                    <Question
                      preview
                      index={index}
                      question={question}
                      key={question.id}
                      status="to_be_filled_collaborator"
                    />
                  ))}
                </Section>
              )
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button
              onClick={onClose}
              id={`closeTemplatePreviewModal-${template.id}`}
            >
              {t("forms.actions.close")}
            </Button>

            <Spacer />

            <Button
              colorScheme="blue"
              onClick={onSelect}
              id={`useTemplate-${template.id}`}
            >
              {t("forms.actions.use")}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
type TemplatePreviewCardProps = {
  template: TemplateFragment;
  onSelect: (id: string) => void;
};
export const TemplatePreviewCard: React.FC<TemplatePreviewCardProps> =
  React.memo(({ template, onSelect }) => {
    const t = useTranslations();
    const [previewOpen, togglePreviewOpen] = useToggle(false);

    const handleSelect = useCallback(
      () => onSelect(template.slug),
      [template, onSelect]
    );

    const [insertTemplate] = useInsertTemplate();

    const handleFavorite = useCallback(
      (active: boolean) => {
        insertTemplate({
          variables: {
            input: { id: template.id, favorite: !template.favorite },
          },
        });
      },
      [insertTemplate, template]
    );

    return (
      <li
        className="flex flex-col justify-between col-span-1 bg-white divide-y divide-gray-200 rounded-lg shadow"
        id={`template-card-${template.id}`}
      >
        <TemplatePreviewModal
          template={template}
          isOpen={previewOpen}
          onClose={togglePreviewOpen}
          onSelect={handleSelect}
        />
        <div className="flex items-start justify-between flex-grow w-full p-6 space-x-6">
          <div className="flex-1">
            <div className="flex items-center space-x-3">
              <H5 className="text-sm font-medium text-gray-900">
                {template.title}
              </H5>
            </div>
            <p className="mt-1 text-sm text-gray-500 truncate">
              {t("templates.questionsNumber", {
                number: template.sections.map((e) => e.questions).flat().length,
              })}
            </p>
          </div>
          <Favorite
            id={`${template.id}-favorite`}
            iconClassName="flex items-center"
            iconProps={{ className: "w-4 h-4" }}
            active={template.favorite}
            onClick={handleFavorite}
          />
        </div>
        <div>
          <div className="flex -mt-px divide-x divide-gray-200">
            <div className="flex flex-1 w-0">
              <div
                role="button"
                id={`template-preview-modal-trigger-${template.id}`}
                tabIndex={0}
                className="relative inline-flex items-center justify-center flex-1 w-0 py-3 -mr-px text-sm font-medium text-gray-700 border border-transparent rounded-bl-lg hover:text-gray-500"
                onClick={togglePreviewOpen}
              >
                <EyeOpenIcon className="w-6 h-6 text-primary-500" />
                <span className="ml-3">{t("forms.actions.view")}</span>
              </div>
            </div>
            <div className="flex flex-1 w-0 -ml-px">
              <div
                role="button"
                id={`template-select-${template.id}`}
                tabIndex={0}
                className="relative inline-flex items-center justify-center flex-1 w-0 py-3 text-sm font-medium text-gray-700 border border-transparent rounded-br-lg hover:text-gray-500"
                onClick={handleSelect}
              >
                <CheckIcon className="w-6 h-6 text-primary-500" />

                <span className="ml-3">{t("forms.actions.use")}</span>
              </div>
            </div>
          </div>
        </div>
      </li>
    );
  });
